import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useMemo } from 'react'
import Info from 'core/components/validatedForm/Info'
import ListTableField from 'core/components/validatedForm/ListTableField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { Labels } from 'k8s/components/common/entity/labels-and-annotations/LabelsOrAnnotations'
import { getCurrentUser, humanReadableSize } from 'openstack/helpers'
import { listFlavors } from '../flavors/actions'
import { flavorsSelector } from '../flavors/selectors'
import { resizeVirtualMachine } from './actions'
import { hasOtherUserVm, isHotplugVm, isVolumeBackedVm } from './helpers'

const flavorColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'id',
    label: 'ID',
    display: false,
  },
  {
    id: 'vcpus',
    label: 'vCPUs',
  },
  {
    id: 'ram',
    label: 'RAM',
    render: (ram) => humanReadableSize(ram * 1024 * 1024),
  },
  {
    id: 'disk',
    label: 'Disk',
    render: (disk) => humanReadableSize(disk * 1024 * 1024 * 1024),
  },
  {
    id: 'extra_specs',
    label: 'Metadata',
    render: (value) => (
      <Labels
        labels={value}
        separator="="
        containerType="table"
        maxVisible={2}
        showMoreButton={true}
      />
    ),
  },
  {
    id: 'visibility',
    label: 'Visibility',
    display: false,
  },
]

export default function ResizeVirtualMachineDialog({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const isHotPluggedVm = isHotplugVm(vm)
  const classes = useStyles()
  const { error, update: resizeFn, updating, reset } = useUpdateAction(resizeVirtualMachine)
  const title = `Resize VM`
  const defaultParams = {
    flavor: [],
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { loading: loadingFlavors, reload: reloadFlavors } = useListAction(listFlavors, {
    params: {},
  })
  const flavors = useSelectorWithParams(flavorsSelector, {} as any)

  // Check if the VM is volume backed
  const isVolumeBackedInstance = useMemo(() => {
    return isVolumeBackedVm(vm)
  }, [vm])

  const eligibleFlavors = useMemo(() => {
    if (!vm?.flavor) {
      return []
    }
    return flavors.filter((flavor) => {
      const baseCondition = vm?.flavor?.original_name !== flavor?.name
      const sizeCondition = isVolumeBackedInstance ? true : flavor?.disk >= vm?.flavor?.disk

      if (!baseCondition || !sizeCondition) {
        return false
      }

      const hasZeroDisk = flavor?.disk === 0
      const hasZeroVcpus = flavor?.vcpus === 0
      const hasZeroRam = flavor?.ram === 0
      const isHotplugCompatible = hasZeroVcpus || hasZeroRam

      // When VM is volume-backed and hotplugged - show only flavors with 0 disk size (diskless flavors)
      if (isVolumeBackedInstance && isHotPluggedVm) {
        return hasZeroDisk && isHotplugCompatible
      }

      // When VM is volume-backed - show only flavors with 0 disk size (diskless flavors)
      if (isVolumeBackedInstance) {
        return hasZeroDisk
      }

      // When VM is hotplugged - show only hotplugging-compatible flavors (vcpus or ram = 0)
      if (isHotPluggedVm) {
        return isHotplugCompatible
      }

      // Otherwise - show normal flavors (non-hotplugging)
      return !hasZeroVcpus && !hasZeroRam
    })
  }, [flavors, vm])

  const handleSubmit = useCallback(async () => {
    const body = {
      resize: {
        flavorRef: params.flavor[0]?.id,
      },
    }
    const { success } = await resizeFn({ id: vm.id, body })
    if (success) {
      handleClose(true)
    }
  }, [params, vm])

  const handleClose = (success?: boolean) => {
    reset()
    onClose(success)
  }

  const { role: userRole, userId } = getCurrentUser()

  return (
    <ModalForm
      title={title}
      onSubmit={handleSubmit}
      onClose={handleClose}
      submitting={updating}
      error={error}
      submitTitle={title}
      panel="dialog"
      maxWidth={1000}
      disableSubmit={!vm?.flavor}
      open
    >
      <div className={classes.container}>
        {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
          <Info error>NOTE: You are performing an operation on another user's VM.</Info>
        )}
        <Text variant="body2">
          You are about to resize <b>{vm.name || vm.id}</b>.
        </Text>
        {vm?.flavor ? (
          <>
            <Text variant="body2">
              Select the flavor you would like to resize this VM to. Resizing a VM will result in
              the VM being rebuilt (powered off, then recreated using the source image on the same
              or different host) using the new flavor. Resize does not change a VM’s cluster. The
              new VM will be deployed in the same virtualized cluster.
            </Text>
            <Text variant="body2">
              <b>Note:</b>{' '}
              {isVolumeBackedInstance
                ? `Only zero-disk flavors are supported for volume-backed instances, as the root disk comes from the attached volume. As a result, the VM disk size will be equal to the volume size`
                : `The disk size of the new flavor must be larger than or equal to the
              current flavor.`}{' '}
              <b>Only eligible flavors are shown below.</b>
            </Text>
            <div className={classes.currentConfigs}>
              <Text variant="body2">
                <b>Current Flavor:</b>
              </Text>
              <Text variant="body2">{`${vm?.flavor?.original_name} - ${vm?.flavor?.vcpus} vCPUs | ${vm?.flavor?.ram}MiB RAM | ${vm?.flavor?.disk}GiB Disk`}</Text>
              <Text variant="body2">
                <b>Cluster:</b>
              </Text>
              <Text variant="body2">
                {vm?.pinned_availability_zone || vm?.['OS-EXT-AZ:availability_zone'] || 'N/A'}
              </Text>
            </div>
            <ListTableField
              id="flavor"
              data={eligibleFlavors}
              loading={loadingFlavors}
              columns={flavorColumns}
              onChange={getParamsUpdater('flavor')}
              value={params.flavor}
              uniqueIdentifier="id"
              searchTargets={['name']}
              emptyText="No eligible flavors found"
              required
              onReload={reloadFlavors}
            />
          </>
        ) : (
          <Text variant="body2">
            <b>Note:</b> The image used to create this VM is not available anymore. VM resizing is
            unavailable.
          </Text>
        )}
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gap: 24,
  },
  currentConfigs: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gap: 16,
  },
}))
